/**
 * VistoeraTASK-00630 — pure draft helpers for plan (and later workspace/member)
 * auth matrices. No React; unit-tested via node-test.
 */

export type AuthDecision = 'ALLOW' | 'DENY' | 'INHERIT'

export type GrantKey = `${'node' | 'channel'}:${string}`

export function grantKey(kind: 'node' | 'channel', subjectId: string): GrantKey {
  return `${kind}:${subjectId}`
}

export function parseGrantKey(key: GrantKey): { kind: 'node' | 'channel'; subjectId: string } {
  const i = key.indexOf(':')
  return { kind: key.slice(0, i) as 'node' | 'channel', subjectId: key.slice(i + 1) }
}

/** Plan grants only store ALLOW|DENY; missing row ≡ untreated (UI shows off). */
export function effectivePlanDecision(decision: string | null | undefined): 'ALLOW' | 'DENY' {
  return decision === 'ALLOW' ? 'ALLOW' : 'DENY'
}

export function isDirty(
  baseline: Record<string, string | null | undefined>,
  draft: Record<string, 'ALLOW' | 'DENY'>,
): boolean {
  for (const [key, value] of Object.entries(draft)) {
    const raw = baseline[key]
    // TASK-00639: unset DB row + draft DENY must be dirty so Save persists
    // DENY (UI already showed off, but resolver treated missing as allow).
    if (raw == null || raw === '') {
      if (value === 'ALLOW' || value === 'DENY') return true
      continue
    }
    if (String(raw) !== value) return true
  }
  return false
}

export function dirtyEntries(
  baseline: Record<string, string | null | undefined>,
  draft: Record<string, 'ALLOW' | 'DENY'>,
): Array<{ kind: 'node' | 'channel'; subjectId: string; decision: 'ALLOW' | 'DENY' }> {
  const out: Array<{ kind: 'node' | 'channel'; subjectId: string; decision: 'ALLOW' | 'DENY' }> = []
  for (const [key, value] of Object.entries(draft)) {
    const raw = baseline[key]
    if (raw == null || raw === '') {
      if (value === 'ALLOW' || value === 'DENY') {
        const { kind, subjectId } = parseGrantKey(key as GrantKey)
        out.push({ kind, subjectId, decision: value })
      }
      continue
    }
    if (String(raw) === value) continue
    const { kind, subjectId } = parseGrantKey(key as GrantKey)
    out.push({ kind, subjectId, decision: value })
  }
  return out
}

export function countAuthorized(
  draft: Record<string, 'ALLOW' | 'DENY'>,
  nodeIds: string[],
  channelIds: string[],
): { nodes: number; channels: number } {
  let nodes = 0
  let channels = 0
  for (const id of nodeIds) {
    if (draft[grantKey('node', id)] === 'ALLOW') nodes += 1
  }
  for (const id of channelIds) {
    if (draft[grantKey('channel', id)] === 'ALLOW') channels += 1
  }
  return { nodes, channels }
}

export type MatrixChannelCell = {
  subject_id: string
  display_name: string
  globally_available?: boolean
  grantable?: boolean
  decision?: string | null
  plan_decision?: string | null
  effective_allowed?: boolean
  restriction_source?: string
  capability?: string | null
}

export type MatrixNodeCell = {
  subject_id: string
  display_name: string
  category?: string | null
  globally_available?: boolean
  grantable?: boolean
  decision?: string | null
  plan_decision?: string | null
  effective_allowed?: boolean
  restriction_source?: string
}

export type MatrixRow = {
  node: MatrixNodeCell
  channels: MatrixChannelCell[]
}

/** Build baseline + draft maps from plan matrix rows. */
export function mapsFromPlanRows(rows: MatrixRow[]): {
  baseline: Record<string, string | null>
  draft: Record<string, 'ALLOW' | 'DENY'>
  nodeIds: string[]
  channelIds: string[]
  channelColumns: Array<{ subject_id: string; display_name: string }>
} {
  const baseline: Record<string, string | null> = {}
  const draft: Record<string, 'ALLOW' | 'DENY'> = {}
  const nodeIds: string[] = []
  const channelColMap = new Map<string, string>()

  for (const row of rows) {
    const nid = row.node.subject_id
    nodeIds.push(nid)
    const nk = grantKey('node', nid)
    baseline[nk] = row.node.decision ?? null
    draft[nk] = effectivePlanDecision(row.node.decision)

    for (const ch of row.channels) {
      const ck = grantKey('channel', ch.subject_id)
      baseline[ck] = ch.decision ?? null
      draft[ck] = effectivePlanDecision(ch.decision)
      if (!channelColMap.has(ch.subject_id)) {
        channelColMap.set(ch.subject_id, ch.display_name || ch.subject_id)
      }
    }
  }

  const channelColumns = [...channelColMap.entries()].map(([subject_id, display_name]) => ({
    subject_id,
    display_name,
  }))
  const channelIds = channelColumns.map((c) => c.subject_id)
  return { baseline, draft, nodeIds, channelIds, channelColumns }
}

/** Channel cell display mode for a row × column. */
export function channelCellMode(
  row: MatrixRow,
  channelId: string,
  nodeAllowed: boolean,
): 'na' | 'blocked_global' | 'blocked_node' | 'editable' {
  const cell = row.channels.find((c) => c.subject_id === channelId)
  if (!cell) return 'na'
  if (!row.node.globally_available || row.node.grantable === false) return 'blocked_global'
  if (!cell.globally_available || cell.grantable === false) return 'blocked_global'
  if (!nodeAllowed) return 'blocked_node'
  return 'editable'
}

/**
 * TASK-00633 — channels for the selected node only.
 * Source of truth: that row's Execution Mapping cells (provider_option_id),
 * never a global cross-product of all providers.
 */
export function channelsForSelectedNode(
  rows: MatrixRow[],
  selectedNodeId: string | null,
): MatrixChannelCell[] {
  if (!selectedNodeId) return []
  const row = rows.find((r) => r.node.subject_id === selectedNodeId)
  return row?.channels ? [...row.channels] : []
}
